import React from "react";
import type {Editor} from "@tiptap/core";
import {useTranslation} from "react-i18next";
import {PRINTOUT_BLOCK_REGISTRY} from "../../../blocks";
import type {PrintoutBlockType} from "../../../blocks";
import {
    getDataListAttrs,
    getDataListSourceDefinition,
} from "../../../blocks/dataList/printoutDataListOptions";
import type {PrintoutDataListAttrs} from "../../../blocks/dataList/printoutDataListOptions";
import {PanelHint, PanelSection} from "../PrintoutPanelSection";
import type {SelectedPrintoutBlock} from "../useSelectedPrintoutBlock";
import ContainerSettings from "./ContainerSettings";
import DataListSettings from "./DataListSettings";
import DividerSettings from "./DividerSettings";
import FieldSettings from "./FieldSettings";
import FolioTableSettings from "./FolioTableSettings";
import ImageSettings from "./ImageSettings";
import QrCodeSettings from "./QrCodeSettings";
import TableSettings from "./TableSettings";
import WritingAreaSettings from "./WritingAreaSettings";

type Props = {
    block: SelectedPrintoutBlock
    editor: Editor
    disabled?: boolean
};

// Some node types have several insert-menu descriptors.
const SECTION_TITLES: Partial<Record<PrintoutBlockType, string>> = {
    printoutDataList: "Data list",
};

export function PrintoutBlockSettingsSection({block, editor, disabled}: Props) {
    const {t} = useTranslation();

    const descriptor = PRINTOUT_BLOCK_REGISTRY.find(entry => entry.type === block.type);
    const title = SECTION_TITLES[block.type] ?? descriptor?.label ?? block.type;
    const description = block.type === "printoutDataList"
        ? getDataListSourceDefinition(
            getDataListAttrs(block.node.attrs as PrintoutDataListAttrs).source,
        ).description
        : descriptor?.description;

    const body = () => {
        switch (block.type) {
            case "printoutDivider":
                return <DividerSettings block={block} disabled={disabled} editor={editor}/>;
            case "printoutImage":
                return <ImageSettings block={block} disabled={disabled} editor={editor}/>;
            case "printoutQrCode":
                return <QrCodeSettings block={block} disabled={disabled} editor={editor}/>;
            case "printoutField":
                return <FieldSettings block={block} disabled={disabled} editor={editor}/>;
            case "printoutWritingArea":
                return <WritingAreaSettings block={block} disabled={disabled} editor={editor}/>;
            case "printoutPageBreak":
                return null;
            case "printoutContainer":
                return <ContainerSettings block={block} disabled={disabled} editor={editor}/>;
            case "table":
                return <TableSettings block={block} disabled={disabled} editor={editor}/>;
            case "printoutDataList":
                return <DataListSettings block={block} disabled={disabled} editor={editor}/>;
            case "printoutFolioTable":
                return <FolioTableSettings block={block} disabled={disabled} editor={editor}/>;
        }
    };

    return (
        <PanelSection icon={descriptor?.icon} title={t(title)}>
            {!!description && <PanelHint>{t(description)}</PanelHint>}
            {body()}
        </PanelSection>
    );
}

export default PrintoutBlockSettingsSection;
